import moment from 'moment';
import { SeasonRegistryResource } from '~~apis/resource';
import RMultilingual from './Multilingual';

export const parseBasicData = (registryInfo) => {
  const startAt = new Date(registryInfo.start_at);
  const endAt = new Date(registryInfo.end_at);

  return {
    description: registryInfo.description,
    is_public: registryInfo.is_public,
    entry_key: registryInfo.entry_key,
    regulation_file_path: registryInfo.regulation_file_path,
    regulation_file_url: registryInfo.regulation_file_url,
    start_at: !!registryInfo.start_at && moment(startAt).isValid() ? moment(startAt).format('YYYY/MM/DD HH:mm') : '',
    end_at: !!registryInfo.end_at && moment(endAt).isValid() ? moment(endAt).format('YYYY/MM/DD HH:mm') : '',
    team_limit: registryInfo.team_limit,
    team_description: registryInfo.team_description,
  };
};

export const parseRequirementData = (registryInfo) => {
  return {
    avatar_ratio_x: registryInfo.avatar_ratio_x,
    avatar_ratio_y: registryInfo.avatar_ratio_y,
    team_columns: JSON.parse(JSON.stringify(registryInfo.team_columns)),
    staff_titles: JSON.parse(JSON.stringify(registryInfo.staff_titles)),
    player_limit: registryInfo.player_limit,
    player_description: registryInfo.player_description,
    player_columns: JSON.parse(JSON.stringify(registryInfo.player_columns)),
  };
};

export const parseFeeData = (registryInfo) => {
  return {
    fee: registryInfo.fee,
    fee_currency: registryInfo.fee_currency,
    fee_description: registryInfo.fee_description,
    payment_method: JSON.parse(JSON.stringify(registryInfo.payment_method)),
    allow_online_payment: registryInfo.allow_online_payment,
  };
};

export const getLinkPrivacy = (isPublic, entryKey) => {
  if (isPublic) {
    return 'PUBLIC';
  }

  return entryKey ? 'KEY_REQUIRED' : 'UNLISTED';
};

export const validBasicData = (basicData) => {
  const errorInfo = {
    error: true,
    message: '',
    data: null,
  };

  const startAt = new Date(basicData.start_at);
  const endAt = new Date(basicData.end_at);
  if (!moment(startAt).isValid() || !moment(endAt).isValid()) {
    errorInfo.message = RMultilingual(`ERROR.DATE_RANGE_FORMAT`);
    return errorInfo;
  }

  if (!moment(startAt).isBefore(endAt)) {
    errorInfo.message = RMultilingual(`ERROR.DATE_RANGE_PERIOD`);
    return errorInfo;
  }

  const teamLimit = parseInt(basicData.team_limit);
  if (Number.isNaN(teamLimit) || teamLimit <= 0) {
    errorInfo.message = RMultilingual(`ERROR.TEAM_LIMIT`);
    return errorInfo;
  }

  return {
    error: false,
    message: '',
    data: {
      ...basicData,
      start_at: moment(startAt).format('YYYY/MM/DD HH:mm'),
      end_at: moment(endAt).format('YYYY/MM/DD HH:mm'),
      team_limit: teamLimit,
    },
  };
};

export const validRequirementData = (requirementData) => {
  const errorInfo = {
    error: true,
    message: '',
    data: null,
  };

  const avatarRatioX = parseFloat(requirementData.avatar_ratio_x);
  const avatarRatioY = parseFloat(requirementData.avatar_ratio_y);
  if (Number.isNaN(avatarRatioX) || Number.isNaN(avatarRatioY) || avatarRatioX <= 0 || avatarRatioY <= 0) {
    errorInfo.message = RMultilingual(`ERROR.AVATAR_RATIO`);
    return errorInfo;
  }

  const teamColumns = [];
  requirementData.team_columns.forEach((column) => {
    const columnName = column.name.trim();
    if (columnName === '') {
      return;
    }

    teamColumns.push({
      ...column,
      name: columnName,
    });
  });

  const staffTitles = [];
  requirementData.staff_titles.forEach((title) => {
    const titleDisplay = title.title.trim();
    if (titleDisplay === '') {
      return;
    }

    const titleColumns = [];
    title.columns.forEach((column) => {
      const columnName = column.name.trim();
      if (columnName === '') {
        return;
      }

      titleColumns.push({
        ...column,
        name: columnName,
      });
    });
    if (titleColumns.length === 0) {
      return;
    }

    staffTitles.push({
      ...title,
      title: titleDisplay,
      columns: titleColumns,
    });
  });

  const playerLimit = parseInt(requirementData.player_limit);
  if (Number.isNaN(playerLimit) || playerLimit <= 0) {
    errorInfo.message = RMultilingual(`ERROR.PLAYER_LIMIT`);
    return errorInfo;
  }

  const playerColumns = [];
  requirementData.player_columns.forEach((column) => {
    const columnName = column.name.trim();
    if (columnName === '') {
      return;
    }

    playerColumns.push({
      ...column,
      name: columnName,
    });
  });

  return {
    error: false,
    message: '',
    data: {
      ...requirementData,
      avatar_ratio_x: avatarRatioX,
      avatar_ratio_y: avatarRatioY,
      team_columns: teamColumns,
      staff_titles: staffTitles,
      player_limit: playerLimit,
      player_columns: playerColumns,
    },
  };
};

export const validFeeData = (feeData) => {
  const errorInfo = {
    error: true,
    message: '',
    data: null,
  };

  const fee = parseFloat(feeData.fee);
  if (Number.isNaN(fee) || fee < 0) {
    errorInfo.message = RMultilingual(`ERROR.FEE`);
    return errorInfo;
  }

  return {
    error: false,
    message: '',
    data: {
      ...feeData,
      fee,
    },
  };
};

export const updateBasicData = (seasonRegistryUniqid, data) => {
  return new Promise((resolve, reject) => {
    SeasonRegistryResource.updateBasic({ seasonRegistryUniqid, data })
      .then(({ data: newRegistryInfo }) => {
        resolve(newRegistryInfo);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const updateRequirementData = (seasonRegistryUniqid, data) => {
  return new Promise((resolve, reject) => {
    SeasonRegistryResource.updateRequirement({ seasonRegistryUniqid, data })
      .then(({ data: newRegistryInfo }) => {
        resolve(newRegistryInfo);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const updateFeeData = (seasonRegistryUniqid, data) => {
  return new Promise((resolve, reject) => {
    SeasonRegistryResource.updateFee({ seasonRegistryUniqid, data })
      .then(({ data: newRegistryInfo }) => {
        resolve(newRegistryInfo);
      })
      .catch((e) => {
        reject(e);
      });
  });
};
